import { useCometState } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { isPersonalizedQuerySource } from '#pplx/frontend/apps/ask/suggest-dropdown/utils/personalizedQuerySource.js';
import { domainFromURL } from '#pplx/frontend/common/files/urls.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useRef } from 'react';
const autoSuggestionAcceptedEvent = EVENT_PAYLOAD;
export function useAskInputSuggestionSubmission({ handleSubmit, inputQueryForAnalytics, fillInput, searchModeOverride, querySource, }) {
    const { track } = useInstrumentation();
    const inputQueryForAnalyticsRef = useRef(inputQueryForAnalytics);
    inputQueryForAnalyticsRef.current = inputQueryForAnalytics;
    const threadSearchMode = useSelectedSearchMode();
    const searchMode = searchModeOverride ?? threadSearchMode;
    const { sidecarSourceTab: { url: sidecarUrl }, } = useCometState();
    const handleSuggestionSubmission = useCallback((suggestion, position) => {
        const newFrontendContextUUID = safeRandomUUID();
        const isPersonalizedVertical = isPersonalizedQuerySource(querySource);
        track(autoSuggestionAcceptedEvent({
            name: 'auto suggestion accepted',
            data: {
                mode: 'dropdown-complete',
                ...(isPersonalizedVertical || inputQueryForAnalyticsRef.current === undefined
                    ? {}
                    : { query: inputQueryForAnalyticsRef.current }),
                ...(isPersonalizedVertical ? {} : { completion: suggestion.query }),
                completion_uuid: suggestion.query_uuid,
                completion_position: position,
                ...(isPersonalizedVertical ? {} : { url: suggestion.url }),
                isNavigational: !!suggestion.url,
                frontendContextUUID: newFrontendContextUUID,
                searchMode,
                isPersonalized: isPersonalizedVertical ? true : suggestion.isPersonalized,
                isPromo: suggestion.isPromo,
                querySource: isPersonalizedVertical ? querySource : suggestion.query_source,
                sourceDomain: import.meta.env.VITE_IS_SIDECAR_BUILD
                    ? domainFromURL(sidecarUrl)
                    : undefined,
            },
        }));
        // Track personalized query click (use submission_query for DDB matching)
        if (!isPersonalizedVertical && suggestion.isPersonalized) {
            fetch('/rest/autosuggest/track-query-clicked', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    query: suggestion.submission_query ?? suggestion.query,
                    search_mode: searchMode,
                    query_source: suggestion.query_source,
                }),
            }).catch(() => { });
        }
        if (suggestion.url) {
            window.open(suggestion.url, '_self');
            return;
        }
        const submitQuery = suggestion.submission_query ?? suggestion.query;
        if ((searchMode === 'research' || searchMode === 'asi') && fillInput) {
            fillInput(submitQuery);
            return;
        }
        handleSubmit({
            query: submitQuery,
            promptSource: 'autosuggest',
            querySource: 'autosuggest',
            newFrontendContextUUID,
            forceEnableBrowserAgent: !!suggestion.isLearnAboutMe,
            enableLearnAboutMe: !!suggestion.isLearnAboutMe,
        });
    }, [handleSubmit, fillInput, querySource, searchMode, track, sidecarUrl]);
    return handleSuggestionSubmission;
}
//# sourceMappingURL=useAskInputSuggestionSubmission.js.map